import { useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const SAMPLE_HEADERS = [
  "pharmacy_name", "region", "commune", "phone", "email",
  "pharmacist_name", "current_software", "status", "source",
];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };
  const split = (l) => {
    const out = [];
    let cur = "", q = false;
    for (const ch of l) {
      if (ch === '"') q = !q;
      else if (ch === "," && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur);
    return out.map((s) => s.trim().replace(/^"|"$/g, ""));
  };
  const headers = split(lines[0]);
  const rows = lines.slice(1).map((l) => {
    const cells = split(l);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cells[i] || ""; });
    return obj;
  });
  return { headers, rows };
}

export default function CSVImport() {
  const [rows, setRows] = useState([]);
  const { user } = useAuth();
  const [headers, setHeaders] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { headers, rows } = parseCSV(ev.target.result);
      setHeaders(headers);
      setRows(rows);
      setResult(null);
    };
    reader.readAsText(f);
  };

  const importNow = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/prospects/import", { rows });
      setResult(data);
      toast.success(`${data.created} prospect(s) créé(s), ${data.skipped_existing} déjà existant(s)`);
    } catch (e) {
      toast.error("Erreur d'import");
    } finally {
      setLoading(false);
    }
  };

  const downloadSample = () => {
    const sampleCSV = SAMPLE_HEADERS.join(",") + "\n" +
      "Pharmacie Example,Dakar,Plateau,+221 77 000 00 00,contact@example.sn,Dr Example,Onepharma,new_lead,csv_import\n";
    const blob = new Blob([sampleCSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prospects-sample.csv";
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="csv-import-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Import CSV</h1>
        <p className="text-sm text-slate-500">Reprise des données Google Sheets / historique Form.</p>
      </div>

      <Card className="p-5">
        <div className="flex flex-col md:flex-row gap-3">
          <Button variant="outline" onClick={downloadSample} data-testid="btn-download-sample">
            <Download size={14} className="mr-2" /> Télécharger le modèle
          </Button>
          <label className="flex-1">
            <input
              type="file"
              accept=".csv"
              onChange={onFile}
              className="hidden"
              data-testid="input-csv-file"
              id="csv-file"
            />
            <Button asChild variant="outline" className="w-full cursor-pointer">
              <label htmlFor="csv-file" className="cursor-pointer">
                <Upload size={14} className="mr-2" /> Sélectionner un CSV
              </label>
            </Button>
          </label>
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Colonnes attendues : <code className="font-mono">{SAMPLE_HEADERS.join(", ")}</code>. Toute autre colonne (ex. réponses de questionnaire) est ignorée : seules les données CRM ci-dessus sont importées.
          {user?.features?.marketing && <> Colonne facultative <code className="font-mono">campagne</code> : le nom exact d'une campagne existante, pour enregistrer l'origine des pharmacies importées.</>}
        </div>
      </Card>

      {rows.length > 0 && !result && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileSpreadsheet className="text-jamora" size={20} />
            <div className="font-display font-semibold">{rows.length} ligne(s) détectée(s)</div>
          </div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg mb-4 max-h-72">
            <table className="text-xs w-full">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider sticky top-0">
                <tr>
                  {headers.map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    {headers.map((h) => <td key={h} className="px-3 py-2">{r[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            data-testid="btn-import-execute"
            onClick={importNow}
            disabled={loading}
          >
            Importer {rows.length} prospects
          </Button>
        </Card>
      )}

      {result && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3 text-jamora">
            <CheckCircle2 size={20} />
            <div className="font-display font-semibold">Import terminé</div>
          </div>
          <div className="text-sm">
            {result.created} créé(s) · {result.skipped_existing} déjà existant(s)
            {result.ambiguous > 0 && <> · {result.ambiguous} à vérifier manuellement (correspondance ambiguë)</>}
            {" · "}{result.errors.length} erreur(s).
          </div>
          {(result.warnings || []).length > 0 && (
            <ul className="mt-3 text-xs text-amber-700 space-y-1" data-testid="import-warnings">
              {result.warnings.map((w, i) => (
                <li key={i}>Ligne {w.row + 1} : {w.warning}</li>
              ))}
            </ul>
          )}
          {result.errors.length > 0 && (
            <ul className="mt-3 text-xs text-red-600 space-y-1">
              {result.errors.map((e, i) => (
                <li key={i}>Ligne {e.row + 1} : {e.error}</li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
